
import { Component, Prop, Vue } from "nuxt-property-decorator";
import HeatingDaysCard from "~/components/molecules/display/HeatingDaysCard.vue";
import TemperatureCard from "~/components/molecules/display/TemperatureCard.vue";
import PelletsConsumption from "~/components/organisms/display/geographySites/PelletsConsumption.vue";
import {
  HeatingDay,
  OilRequirements,
  WeatherData,
} from "~/types/OilRequirement";
import {
  calculateAbsoluteDifference,
  calculatePercentageDifference,
  currentMonth,
  formatHeatingDays,
  formatNumberToString,
  formatPercentageWithoutZeros,
  getDirection,
  getPreviousYear,
} from "~/utils/oilRequirementUtils";

@Component({
  components: {
    TemperatureCard,
    HeatingDaysCard,
    PelletsConsumption,
  },
})
export default class PelletsStatistics extends Vue {
  @Prop() city!: string;
  @Prop() zipcode!: string;
  @Prop() oilRequirements!: OilRequirements;

  maxHeatingDays = 365;
  minTemp = -5;
  maxTemp = 35;

  formatPercentageWithoutZeros = formatPercentageWithoutZeros;
  formatNumberToString = formatNumberToString;
  formatHeatingDays = formatHeatingDays;
  currentMonth = currentMonth;

  get cityWeatherData(): Partial<WeatherData> {
    return this.oilRequirements?.weatherData ?? {};
  }

  get temperatureDifference2023() {
    const avg2023 = this.cityWeatherData.outdoorTemp_Avg2023 ?? 0;
    const avgLongTerm = this.cityWeatherData.outdoorTemp_AvgLongTermAvg ?? 0;
    const diff = avg2023 - avgLongTerm;

    return {
      percentage: parseFloat(
        calculatePercentageDifference(avg2023, avgLongTerm),
      ),
      direction: getDirection(diff, "überschritt", "unterschritt"),
    };
  }

  get temperatureDifferenceCurrentMonth() {
    const currTemp = this.cityWeatherData.currentMonthData?.outdoorTemp ?? 0;
    const longTermAvg =
      this.cityWeatherData.currentMonthData?.outdoorTemp_LongTermAvg ?? 0;
    const diff = currTemp - longTermAvg;

    return {
      percentage: parseFloat(
        calculatePercentageDifference(currTemp, longTermAvg),
      ),
      direction: getDirection(diff, "über", "unter"),
    };
  }

  get heatingDaysDifference() {
    const days2023 = this.cityWeatherData.heatingDays_Total2023 ?? 0;
    const daysLongTerm = this.cityWeatherData.heatingDays_TotalLongTermAvg ?? 0;
    return parseFloat(calculatePercentageDifference(days2023, daysLongTerm));
  }

  get heatingTempDifference() {
    const temp2023 = this.cityWeatherData.outdoorTempHeatingDays_Avg2023 ?? 0;
    const tempLongTerm =
      this.cityWeatherData.outdoorTempHeatingDays_AvgLongTermAvg ?? 0;
    return parseFloat(calculatePercentageDifference(temp2023, tempLongTerm));
  }

  get heatingDaysDifferenceMay() {
    const mayDays = this.cityWeatherData.currentMonthData?.heatingDays ?? 0;
    const mayLongTerm =
      this.cityWeatherData.currentMonthData?.heatingDays_LongTermAvg ?? 0;
    const diff = calculateAbsoluteDifference(mayDays, mayLongTerm);

    return {
      difference: diff,
      percentage: parseFloat(
        calculatePercentageDifference(mayDays, mayLongTerm),
      ),
    };
  }

  get heatingTempDifferenceMay() {
    const mayTemp =
      this.cityWeatherData.currentMonthData?.outdoorTempHeatingDays ?? 0;
    const longTerm =
      this.cityWeatherData.currentMonthData
        ?.outdoorTempHeatingDays_LongTermAvg ?? 0;
    return parseFloat(calculatePercentageDifference(mayTemp, longTerm));
  }

  get heatingDays(): HeatingDay[] {
    return [
      {
        label: `Durchschnitt ${getPreviousYear()}`,
        days: formatHeatingDays(this.cityWeatherData.heatingDays_Total2023),
      },
      {
        label: "Letzte 20 Jahre",
        days: formatHeatingDays(
          this.cityWeatherData.heatingDays_TotalLongTermAvg,
        ),
      },
    ];
  }

  get outdoorTemperature(): HeatingDay[] {
    return [
      {
        label: `Durchschnitt ${getPreviousYear()}`,
        temperature: this.cityWeatherData.outdoorTemp_Avg2023,
      },
      {
        label: "Letze 20 Jahre",
        temperature: this.cityWeatherData.outdoorTemp_AvgLongTermAvg,
      },
    ];
  }

  get heatDaysTemperature(): HeatingDay[] {
    return [
      {
        label: `Durchschnitt ${getPreviousYear()}`,
        temperature: this.cityWeatherData.outdoorTempHeatingDays_Avg2023,
      },
      {
        label: "Letze 20 Jahre",
        temperature: this.cityWeatherData.outdoorTempHeatingDays_AvgLongTermAvg,
      },
    ];
  }

  get annualTemperatureComparisonText(): string {
    const temp2023 = this.formatNumberToString(
      this.cityWeatherData.outdoorTemp_Avg2023,
    );
    const tempLongTerm = this.formatNumberToString(
      this.cityWeatherData.outdoorTemp_AvgLongTermAvg,
    );
    const diff = this.formatPercentageWithoutZeros(
      this.temperatureDifference2023.percentage,
      false,
      false,
    );

    return `In ${this.zipcode} ${
      this.city
    } lag die durchschnittliche Außentemperatur im Jahr ${getPreviousYear()} bei ${temp2023}°C. Damit ${
      this.temperatureDifference2023.direction
    } sie den Durchschnitt der letzten 20 Jahre von ${tempLongTerm}°C um ${diff}%.`;
  }

  get heatingDaysComparisonText(): string {
    const daysLongTerm = formatHeatingDays(
      this.cityWeatherData.heatingDays_TotalLongTermAvg,
    );
    const tempLongTerm = formatNumberToString(
      this.cityWeatherData.outdoorTempHeatingDays_AvgLongTermAvg,
    );
    const days2023 = formatHeatingDays(
      this.cityWeatherData.heatingDays_Total2023,
    );
    const temp2023 = formatNumberToString(
      this.cityWeatherData.outdoorTempHeatingDays_Avg2023,
    );
    const daysDiffPercentage = this.formatPercentageWithoutZeros(
      this.heatingDaysDifference,
      false,
    );
    const tempDiffPercentage = this.formatPercentageWithoutZeros(
      this.heatingTempDifference,
      false,
    );

    const currentMonthName = currentMonth();
    const daysCurrentMonth = formatHeatingDays(
      this.cityWeatherData.currentMonthData?.heatingDays ?? 0,
    );
    const daysLongTermMonth = formatHeatingDays(
      this.cityWeatherData.currentMonthData?.heatingDays_LongTermAvg ?? 0,
    );
    const diffDaysMonth = Math.abs(this.heatingDaysDifferenceMay.difference);
    const daysDiffMonthPercentage = this.formatPercentageWithoutZeros(
      this.heatingDaysDifferenceMay.percentage,
      false,
    );
    const tempCurrentMonth = formatNumberToString(
      this.cityWeatherData.currentMonthData?.outdoorTempHeatingDays ?? 0,
    );
    const tempLongTermMonth = formatNumberToString(
      this.cityWeatherData.currentMonthData
        ?.outdoorTempHeatingDays_LongTermAvg ?? 0,
    );
    const tempDiffMonthPercentage = this.formatPercentageWithoutZeros(
      this.heatingTempDifferenceMay,
      false,
    );

    // Conditional text for month difference
    const monthDiffText =
      diffDaysMonth === 0
        ? `Das entspricht genauso vielen Heiztagen wie im Durchschnitt der letzten 20 Jahre.`
        : `Das sind ${Math.abs(diffDaysMonth)} Tage ${
            diffDaysMonth > 0 ? "mehr" : "weniger"
          } (${daysDiffMonthPercentage}%) im Vergleich zu den mittleren ${daysLongTermMonth} Heiztagen im ${currentMonthName} der letzten 20 Jahre.`;

    const monthTempDiffText =
      this.heatingDaysDifferenceMay.difference === 0
        ? ""
        : ` An Heiztagen des ${currentMonthName} lag die durchschnittliche Außentemperatur in den vergangenen 20 Jahren bei ${tempLongTermMonth}°C, während sie im ${currentMonthName} ${getPreviousYear()} ${tempCurrentMonth}°C (${tempDiffMonthPercentage}) betrug.`;

    return `Die Anzahl der Heiztage lag in ${this.zipcode} ${
      this.city
    } in den vergangenen 20 Jahren durchschnittlich bei ${daysLongTerm} Tagen pro Jahr. Dabei betrug die mittlere Außentemperatur an Heiztagen ${tempLongTerm}°C. Im Jahr ${getPreviousYear()} wurde in ${
      this.zipcode
    } ${
      this.city
    } hingegen an durchschnittlich ${days2023} Tagen geheizt, was einer Verringerung um ${daysDiffPercentage} entspricht. An diesen Tagen war die Außentemperatur durchschnittlich ${temp2023}°C (${tempDiffPercentage}). Im ${currentMonthName} ${getPreviousYear()} wurde in ${
      this.zipcode
    } ${
      this.city
    } im Durchschnitt an ${daysCurrentMonth} Tagen geheizt. ${monthDiffText}${monthTempDiffText}`;
  }
}
